import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon, IconNameEnum, IconTypeEnum } from "../../../components/Icon";
import { Slider, SliderVariant } from "../../../components/Slider";
import { TextInput } from "../../../components/TextInput";
import { Tooltip } from "../../../components/Tooltip";
import { CurrencySymbol } from "../../../types/currency";
import { calculateStepSize } from "../../../utils/step";

import styles from "./BillingFeeInput.module.scss";

interface BillingFeeInputProps {
  value: number;
  onChange: (value: number) => void;
  defaultValue: number;
  minValue: number;
  maxValue: number;
  maxDecimalPlaces?: number;
  iconName?: IconNameEnum;
  sliderVariant?: SliderVariant;
  label: string;
  description?: string;
  children?: React.ReactNode;
  isDisabled?: boolean;
  unit?: string;
}

export const BillingFeeInput: React.FC<BillingFeeInputProps> = ({
  value,
  onChange,
  defaultValue,
  minValue,
  maxValue,
  maxDecimalPlaces = 2,
  iconName = IconNameEnum.trash,
  sliderVariant,
  label,
  description,
  isDisabled = false,
  unit = CurrencySymbol.Eur,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(String(value));

  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const handleInputBlur = () => {
    const parsedValue = parseFloat(inputValue);

    if (isNaN(parsedValue)) {
      setInputValue(String(value));
      return;
    }

    const clampedValue = Math.min(Math.max(parsedValue, minValue), maxValue);
    const roundedValue = Number(clampedValue.toFixed(maxDecimalPlaces));

    onChange(roundedValue);
    setInputValue(roundedValue.toFixed(maxDecimalPlaces));
  };

  const handleReset = () => {
    onChange(defaultValue);
    setInputValue(String(defaultValue));
  };

  return (
    <div className={styles.container}>
      <div className={styles.iconContainer}>
        <Icon name={iconName} />
      </div>
      <div className={styles.sliderContainer}>
        <div className={styles.label}>
          {label}
          {description && (
            <Tooltip content={description}>
              <Icon name={IconNameEnum.info} type={IconTypeEnum.grey} />
            </Tooltip>
          )}
        </div>
        <Slider
          value={value}
          onChange={(value) => onChange(value)}
          min={minValue}
          max={maxValue}
          steps={calculateStepSize(maxDecimalPlaces)}
          marks={[{ value: defaultValue }]}
          variant={sliderVariant}
          isDisabled={isDisabled}
        />
      </div>
      <div className={styles.inputContainer}>
        <button className={styles.resetButton} onClick={handleReset} disabled={isDisabled}>
          {t("billingFeeInput.reset")}
        </button>
        <TextInput
          type="number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleInputBlur}
          endItem={<div className={styles.unit}>{unit}</div>}
          isDisabled={isDisabled}
        />
      </div>
    </div>
  );
};
